import { useCallback, useEffect, useRef } from 'react';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  buildProjectFilesTourSteps,
  dispatchProjectFilesTourEvent,
  hasSeenProjectFilesTour,
  markProjectFilesTourSeen,
  type ProjectFilesTourScript,
  type ProjectFilesTourStep,
} from '../../utils/projectFilesTourSteps';
import '../leads/LeadsHubTour.css';

export interface UseProjectFilesTourOptions {
  /** Files tab is mounted and its data has loaded. */
  enabled: boolean;
  script: ProjectFilesTourScript;
  uid?: string;
  /** "Classic files view" toggle — skips the multi-view steps. */
  classicView?: boolean;
}

/** Delay between asking `ProjectFiles` to switch views and highlighting the target. */
const VIEW_SWITCH_DELAY_MS = 180;

/** Ask the Files tab to mount the view a step lives on. Returns true when a switch was requested. */
function prepareStep(step: ProjectFilesTourStep | undefined): boolean {
  if (!step?.view) return false;
  dispatchProjectFilesTourEvent({ type: 'setView', view: step.view, folderPath: step.folderPath });
  return true;
}

/**
 * driver.js walkthrough for the Files tab. Auto-starts once per user/script on
 * first visit (localStorage), and can be replayed from {@link FilesHelpButton}.
 * Steps that live on another Files view ask `ProjectFiles` to switch views via
 * a window event before moving, so the highlighted element is actually mounted.
 */
export function useProjectFilesTour({ enabled, script, uid, classicView = false }: UseProjectFilesTourOptions) {
  const driverRef = useRef<Driver | null>(null);
  const pendingStartRef = useRef<number | null>(null);
  const autoStartedRef = useRef(false);

  const destroyTour = useCallback(() => {
    if (pendingStartRef.current !== null) {
      window.clearTimeout(pendingStartRef.current);
      pendingStartRef.current = null;
    }
    driverRef.current?.destroy();
    driverRef.current = null;
  }, []);

  const driveSteps = useCallback(
    (steps: ProjectFilesTourStep[]) => {
      const driverObj = driver({
        steps,
        showProgress: steps.length > 2,
        progressText: '{{current}} of {{total}}',
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        doneBtnText: 'Got it',
        popoverClass: 'lh-tour-popover',
        smoothScroll: true,
        stagePadding: 8,
        stageRadius: 6,
        allowClose: true,
        overlayOpacity: 0.45,
        onNextClick: (_el, _step, { driver: d, state }) => {
          if (d.isLastStep()) {
            d.destroy();
            return;
          }
          const nextIndex = (state.activeIndex ?? 0) + 1;
          if (prepareStep(steps[nextIndex])) {
            window.setTimeout(() => d.moveNext(), VIEW_SWITCH_DELAY_MS);
          } else {
            d.moveNext();
          }
        },
        onPrevClick: (_el, _step, { driver: d, state }) => {
          const prevIndex = (state.activeIndex ?? 0) - 1;
          if (prevIndex < 0) return;
          if (prepareStep(steps[prevIndex])) {
            window.setTimeout(() => d.movePrevious(), VIEW_SWITCH_DELAY_MS);
          } else {
            d.movePrevious();
          }
        },
        onCloseClick: (_el, _step, { driver: d }) => d.destroy(),
        onDestroyed: () => {
          driverRef.current = null;
          markProjectFilesTourSeen(uid, script);
          dispatchProjectFilesTourEvent({ type: 'end' });
        },
      });
      driverRef.current = driverObj;
      driverObj.drive();
    },
    [script, uid],
  );

  const startTour = useCallback(() => {
    destroyTour();
    // Deliverables steps are only built when their elements exist, so mount that
    // view first (the user may have left the tab on Submittals / folders).
    // `end` restores whatever they were looking at.
    dispatchProjectFilesTourEvent({ type: 'start' });
    dispatchProjectFilesTourEvent({ type: 'setView', view: 'deliverables' });
    pendingStartRef.current = window.setTimeout(() => {
      pendingStartRef.current = null;
      const steps = buildProjectFilesTourSteps({ script, classicView });
      if (steps.length === 0) {
        dispatchProjectFilesTourEvent({ type: 'end' });
        return;
      }
      driveSteps(steps);
    }, VIEW_SWITCH_DELAY_MS);
    return true;
  }, [destroyTour, driveSteps, script, classicView]);

  useEffect(() => () => destroyTour(), [destroyTour]);

  useEffect(() => {
    if (!enabled || autoStartedRef.current || hasSeenProjectFilesTour(uid, script)) return;
    autoStartedRef.current = true;
    const timer = window.setTimeout(() => startTour(), 1100);
    return () => window.clearTimeout(timer);
  }, [enabled, script, uid, startTour]);

  return { startTour };
}
